export interface CsvRecipient {
  mobile: string;
  ceaNumber?: string;
  name?: string;
  /**
   * Ordered variable values parsed from columns named `var1`, `var2`, …
   * Index 0 corresponds to `{{1}}` in the template body.
   */
  variables?: string[];
  [key: string]: string | string[] | undefined;
}

const CEA_HEADER_KEYS = ['ceanumber', 'cea', 'cea_number', 'cea number'];

/**
 * Parse a CSV file text into recipients. The first column is treated as the
 * mobile number. If a header row is detected, additional columns are keyed by
 * their header name (e.g. "name").
 *
 * Very small purpose-built parser — handles quoted values with commas and
 * escaped double quotes, without any third-party dependency.
 */
export function parseCsvRecipients(text: string): CsvRecipient[] {
  const rows = parseCsvRows(text);
  if (rows.length === 0) return [];

  let headers: string[] | null = null;
  const first = rows[0];
  const firstLooksLikeHeader = first.some((cell) =>
    /[a-zA-Z]/.test(cell) && !/^\+?\d[\d\s-]+$/.test(cell.trim()),
  );
  let startIndex = 0;
  if (firstLooksLikeHeader) {
    headers = first.map((h) => h.trim().toLowerCase());
    startIndex = 1;
  }

  const recipients: CsvRecipient[] = [];
  for (let r = startIndex; r < rows.length; r += 1) {
    const row = rows[r];
    if (row.every((cell) => cell.trim() === '')) continue;

    const recipient: CsvRecipient = { mobile: '' };
    if (headers) {
      const stringByKey: Record<string, string> = {};
      headers.forEach((key, idx) => {
        const value = (row[idx] || '').trim();
        stringByKey[key] = value;
        recipient[key] = value;
      });
      const mobileKey =
        headers.find((h) => h === 'mobile' || h === 'phone' || h === 'number' || h === 'msisdn') ||
        headers[0];
      recipient.mobile = (stringByKey[mobileKey] || row[0] || '').trim();
      const ceaKey = headers.find((h) => CEA_HEADER_KEYS.includes(h));
      if (ceaKey) recipient.ceaNumber = (stringByKey[ceaKey] || '').trim();

      // Capture ordered variable values from columns named var1, var2, …
      const varEntries: Array<{ index: number; value: string }> = [];
      headers.forEach((key) => {
        const match = /^var(\d+)$/.exec(key);
        if (match) {
          const idx = Number(match[1]);
          if (Number.isFinite(idx) && idx >= 1) {
            varEntries.push({ index: idx, value: stringByKey[key] ?? '' });
          }
        }
      });
      if (varEntries.length > 0) {
        const maxIndex = varEntries.reduce((m, e) => (e.index > m ? e.index : m), 0);
        const variables = new Array<string>(maxIndex).fill('');
        for (const entry of varEntries) variables[entry.index - 1] = entry.value;
        recipient.variables = variables;
      }
    } else {
      // No header row: assume column order [mobile, ceaNumber, name?].
      recipient.mobile = (row[0] || '').trim();
      if (row[1]) recipient.ceaNumber = row[1].trim();
      if (row[2]) recipient.name = row[2].trim();
    }
    if (recipient.mobile) recipients.push(recipient);
  }

  return recipients;
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let current: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === ',') {
      current.push(cell);
      cell = '';
      continue;
    }
    if (char === '\n' || char === '\r') {
      // Handle \r\n
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      current.push(cell);
      rows.push(current);
      current = [];
      cell = '';
      continue;
    }
    cell += char;
  }

  if (cell.length > 0 || current.length > 0) {
    current.push(cell);
    rows.push(current);
  }
  return rows;
}
